import { Grid, HStack, Text, useDisclosure, VStack } from "@chakra-ui/react";
import { lazy, Suspense, useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import { Empty } from "../components/card/Empty";
import { CardProductMyStore } from "../components/dashboard_reseller/CardProductMyStore";
import { MenuSort } from "../components/menu/MenuSort";
import Pagination from "../components/Pagination";
import ResellersModal from "../components/product_page/ResellersModal";
import { CardSkeleton } from "../components/skeleton/CardSkeleton";
import { menuSortProductSimple } from "../constants/ListMenuSort";
import { PaginationInterface } from "../constants/Pagination";
import {
  CardProductMyStoreInterface,
  ProductInterface,
} from "../constants/Product";
import { SelectOption } from "../constants/SelectOption";
import useCustomToast from "../lib/useCustomToast";
import useScreenWidth from "../lib/useScreenWidth";
import { axiosPublic } from "../utils/AxiosInstance";
import { MetaTag } from "../utils/MetaTag";

const CategoryFilter = lazy(() => import("../components/menu/CategoryFilter"));
const FilterDrawer = lazy(() => import("../components/drawer/FilterDrawer"));

const ProductPage = () => {
  const [data, setData] = useState<CardProductMyStoreInterface[] | undefined>(
    undefined
  );
  const [pagination, setPagination] = useState<PaginationInterface | undefined>(
    undefined
  );
  const location = useLocation();
  const [selectedSlug, setSelectedSlug] = useState<string>("");
  const [loaded, setLoaded] = useState<boolean>(false);
  const [page, setPage] = useState<number>(1);
  const [sort, setSort] = useState<string>("");
  const [filter, setFilter] = useState<SelectOption | undefined>(undefined);
  const toast = useCustomToast();
  const sw = useScreenWidth();
  const { isOpen, onOpen, onClose } = useDisclosure();

  const queryParams = new URLSearchParams(location.search);
  const searchUrl = queryParams.get("search") || "";

  useEffect(() => {
    const categoryId = filter?.id ? filter?.id : "";

    axiosPublic
      .get(
        `/v1/landing/product?page=${page}&search=${searchUrl}&sort=${sort}&category=${categoryId}`
      )
      .then((res) => {
        const formattedData = res.data.data.data.map(
          (item: ProductInterface) => ({
            id: item.id,
            name: item.name,
            category: item.category,
            image: item.main_picture,
            price: item.lowest_price,
            slug: item.slug,
          })
        );

        setData(formattedData);
        setPagination(res.data.data.paginator);
      })
      .catch((err) => {
        toast({
          description: err?.response?.data?.message || "",
        });
      })
      .finally(() => {
        setLoaded(true);
      });
  }, [filter?.id, page, searchUrl, sort]);

  return (
    <>
      <MetaTag
        title="Daftar Produk | BisnisLink"
        description="Temukan berbagai produk yang sesuai dengan kebutuhan Anda di BisnisLink."
      />

      <HStack
        className="root-product"
        w={"100%"}
        py={5}
        px={{
          base: "12px",
          sm: "24px",
          md: "32px",
          lg: "40px",
          xl: "48px",
          "2xl": 0,
        }}
        alignItems={"start"}
      >
        {sw > 768 && (
          <Suspense fallback={<CardSkeleton row={1} height={435} />}>
            <CategoryFilter
              mt={4}
              onSelected={(selectedFilter) => {
                setFilter(selectedFilter);
              }}
              onResetFilter={(reset) => {
                setFilter(undefined);
              }}
            />
          </Suspense>
        )}

        <VStack
          className="layout-product-supplier"
          w={"100%"}
          mt={4}
          px={sw > 768 ? 4 : 0}
          pb={sw > 768 ? 4 : 0}
          align={"stretch"}
        >
          <HStack w={"100%"} justifyContent={"end"}>
            {searchUrl && (
              <HStack w={"100%"} justifySelf={"start"}>
                <Text textStyle={"b-sm"}>Menampilkan pencarian untuk</Text>

                <Text textStyle={"b-sm-sb"}>{`"${searchUrl}"`}</Text>
              </HStack>
            )}

            <HStack gap={[1, 2, 4]} justifySelf={"end"}>
              {sw < 768 && (
                <Suspense fallback={<CardSkeleton row={1} height={40} />}>
                  <FilterDrawer
                    onSelected={(select) => {
                      setFilter(select);
                    }}
                    onResetFilter={(reset) => {
                      setFilter(undefined);
                    }}
                  />
                </Suspense>
              )}

              <MenuSort
                item={menuSortProductSimple}
                onSelected={(selected) => {
                  setSort(`${selected?.note}`);
                }}
                children={undefined}
              />
            </HStack>
          </HStack>

          {filter && (
            <HStack gap={1}>
              <Text fontSize={[10, null, 12]}>
                Menampilkan pencarian untuk filter
              </Text>
              <Text fontSize={[10, null, 12]} fontWeight={"semibold"}>
                {`"${filter?.name}"`}
              </Text>
            </HStack>
          )}

          {!loaded ? (
            <CardSkeleton row={3} column={2} height={300} mt={4} />
          ) : (
            <VStack w={"100%"}>
              {data && data.length > 0 ? (
                <Grid
                  w={"100%"}
                  templateColumns={"repeat(auto-fill, minmax(160px, 1fr))"}
                  gap={4}
                  mt={4}
                >
                  {data.map((item) => (
                    <CardProductMyStore
                      key={item.id}
                      data={item}
                      onClickItem={(id, prefix, slug) => {
                        setSelectedSlug(slug);
                        onOpen();
                      }}
                    />
                  ))}
                </Grid>
              ) : (
                <Empty />
              )}

              {loaded && pagination && data && data?.length > 0 && (
                <Pagination
                  mt={4}
                  totalItems={pagination.total}
                  pageSize={pagination.per_page}
                  onPageChange={(page) => {
                    setPage(page);
                  }}
                />
              )}
            </VStack>
          )}
        </VStack>
      </HStack>

      <ResellersModal
        isOpen={isOpen}
        onClose={onClose}
        productSlug={selectedSlug}
      />
    </>
  );
};

export default ProductPage;
